import { AddIcon, ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Collapse,
  Heading,
  IconButton,
  Text,
  useColorMode,
  useDisclosure,
} from "@chakra-ui/react";
import {
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  PointElement,
  RadialLinearScale,
  Tooltip,
  TooltipItem,
} from "chart.js";
import { useEffect, useMemo, useState } from "react";
import { Radar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { AssessmentDto } from "../../../../autogen/bff-api";
import { displayDateWithTime } from "../../../../common/formatting/displayDateWithTime";
import { displayPersonName } from "../../../../common/persons/displayPersonName";
import {
  ASSESSMENT_RADAR_FILL_COLOR,
  ASSESSMENT_RADAR_LINE_COLOR,
  ASSESSMENT_RADAR_POINT_ACCENT_COLOR,
  CHART_GRID_COLOR,
  CHART_TEXT_COLOR,
} from "../../../../config";

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

interface OrganizationAssessmentProps {
  companyName: string;
  assessments: AssessmentDto[];
  templateId?: string;
  onCreateAssessment?: () => void;
}

export const OrganizationAssessment = ({
  companyName,
  assessments,
  onCreateAssessment,
}: OrganizationAssessmentProps) => {
  const { t } = useTranslation();
  const { colorMode } = useColorMode();
  const { isOpen: showComments, onToggle: toggleComments } = useDisclosure();

  const textColor = CHART_TEXT_COLOR[colorMode];
  const gridColor = CHART_GRID_COLOR[colorMode];

  const sortedAssessments = useMemo(() => {
    return [...assessments].sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateA - dateB; // Sort ascending (oldest first)
    });
  }, [assessments]);

  const [currentAssessmentIndex, setCurrentAssessmentIndex] = useState(0);

  useEffect(() => {
    if (sortedAssessments.length > 0) {
      setCurrentAssessmentIndex(sortedAssessments.length - 1);
    }
  }, [sortedAssessments.length]);

  const assessmentData = sortedAssessments[currentAssessmentIndex];

  const dimensions = assessmentData?.dimensionScores || [];
  const dimensionScores = dimensions.length > 0 ? dimensions.map((d) => d.directScore) : null;
  const dimensionLabels = dimensions.length > 0 ? dimensions.map((d) => d.dimensionName || d.dimensionId) : [];

  const data = dimensionScores
    ? {
        labels: dimensionLabels,
        datasets: [
          {
            label: companyName,
            data: dimensionScores,
            backgroundColor: ASSESSMENT_RADAR_FILL_COLOR,
            borderColor: ASSESSMENT_RADAR_LINE_COLOR,
            borderWidth: 2,
            pointBackgroundColor: ASSESSMENT_RADAR_LINE_COLOR,
            pointBorderColor: ASSESSMENT_RADAR_POINT_ACCENT_COLOR,
            pointHoverBackgroundColor: ASSESSMENT_RADAR_POINT_ACCENT_COLOR,
            pointHoverBorderColor: ASSESSMENT_RADAR_LINE_COLOR,
          },
        ],
      }
    : null;

  const options = {
    scales: {
      r: {
        angleLines: {
          color: gridColor,
        },
        grid: {
          color: gridColor,
        },
        pointLabels: {
          color: textColor,
          font: {
            size: 11,
          },
        },
        ticks: {
          backdropColor: "transparent",
          color: textColor,
          z: 100,
        },
        suggestedMin: 0,
        suggestedMax: 5,
      },
    },
    plugins: {
      legend: {
        position: "top" as const,
        labels: {
          color: textColor,
        },
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem: TooltipItem<"radar">) {
            return `${tooltipItem.dataset.label || ""}: ${tooltipItem.raw}`;
          },
        },
      },
    },
    maintainAspectRatio: false,
  };

  if (assessments.length === 0) {
    return (
      <Box
        p={4}
        borderWidth="1px"
        borderRadius="lg"
        backgroundColor="smBackground"
        width="full"
        height="full"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Text>{t("No assessments available for this template")}</Text>
      </Box>
    );
  }

  return (
    <Box p={4} borderWidth="1px" rounded="lg" backgroundColor="smBackground" width="full" height="full">
      {assessmentData ? (
        <>
          <Box mb={4}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Badge colorScheme="blue" fontSize="xs">
                {assessments.length} {assessments.length === 1 ? t("assessment") : t("assessments")}
              </Badge>

              <Box display="flex" alignItems="center">
                {sortedAssessments.length > 1 && (
                  <ButtonGroup size="xs" isAttached variant="outline">
                    <Button
                      onClick={() => setCurrentAssessmentIndex((prev) => Math.max(0, prev - 1))}
                      isDisabled={currentAssessmentIndex === 0}
                    >
                      {t("Previous")}
                    </Button>
                    <Button
                      onClick={() =>
                        setCurrentAssessmentIndex((prev) => Math.min(sortedAssessments.length - 1, prev + 1))
                      }
                      isDisabled={currentAssessmentIndex === sortedAssessments.length - 1}
                    >
                      {t("Next")}
                    </Button>
                  </ButtonGroup>
                )}
                {onCreateAssessment && (
                  <IconButton
                    aria-label={t("Create new assessment")}
                    icon={<AddIcon fontSize="0.8em" />}
                    size="xs"
                    ml={2}
                    variant="ghost"
                    onClick={onCreateAssessment}
                  />
                )}
              </Box>
            </Box>
            <Heading size="sm" py="2">
              {assessmentData.template.name}{" "}
              <Text as="span" fontSize="xs" color="smMuted">
                v{assessmentData.template.templateVersion}
              </Text>
            </Heading>

            <Box mb={4}>
              <Text fontSize="sm">{assessmentData.name}</Text>
              <Text fontSize="sm" color="smMuted">
                {assessmentData.createdAt && displayDateWithTime(assessmentData.createdAt)}
                {assessmentData.assessor && ` • ${displayPersonName(assessmentData.assessor)}`}
              </Text>
            </Box>

            <Box height="300px" mb={6}>
              {data && <Radar data={data} options={options} />}
            </Box>
            {dimensions.length > 0 && dimensions.some((d) => d.comment) && (
              <Box mt={4}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                  <Heading size="sm">{t("Comments")}</Heading>
                  <IconButton
                    size="sm"
                    aria-label={showComments ? t("Hide comments") : t("Show comments")}
                    icon={showComments ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    onClick={toggleComments}
                    variant="ghost"
                  />
                </Box>
                <Collapse in={showComments} animateOpacity>
                  <Box maxH="300px" overflowY="auto" pr={2}>
                    {dimensions.map((dimension) =>
                      dimension.comment ? (
                        <Box
                          key={dimension.dimensionId}
                          mb={4}
                          p={3}
                          borderWidth="1px"
                          borderRadius="md"
                          borderColor="smBorder"
                          bg={colorMode === "dark" ? "whiteAlpha.50" : "gray.50"}
                        >
                          <Text fontWeight="medium" mb={1} fontSize="md">
                            {dimension.dimensionName || dimension.dimensionId} {/* Display dimension name */}
                            <Text as="span" fontWeight="normal" color="blue.500" ml={2}>
                              {dimension.directScore}
                              {dimension.scoreRange?.maxValue ? `/${dimension.scoreRange?.maxValue}` : ""}
                            </Text>
                          </Text>
                          <Text fontSize="sm">{dimension.comment}</Text>
                        </Box>
                      ) : null
                    )}
                  </Box>
                </Collapse>
              </Box>
            )}
          </Box>
        </>
      ) : (
        <Box display="flex" justifyContent="center" alignItems="center" height="300px">
          <Text>{t("No assessments available for this organization yet")}.</Text>
        </Box>
      )}
    </Box>
  );
};
